// client/src/contexts/BugReportsContext.tsx
//
// Shared store for user-submitted bug/error reports. The "Report a problem"
// button (TopBar) adds to it; the Dev Team board reads from it. Backed by
// localStorage so a submitted report survives navigation/reload.
//
// PREVIEW / stand-in: this is in-browser only (per-browser, not shared across
// users). When the backend lands, swap this for a fetch of Feedback.BugReports +
// a POST to /api/bug-report — the hook surface (useBugReports) stays the same, so
// consumers (BugReporter, DevTeam) don't change.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type BugStatus = 'inbox_triage' | 'open' | 'in_progress' | 'resolved';

export type BugReport = {
  bugReportId: number;
  contextApp: string;
  issue: string;
  description?: string;
  steps?: string;
  submittedBy: string;
  status: BugStatus;
  assignedTo: string | null;
  when: string; // display-only
  // When the report was resolved (func stamps ReviewedAt on resolve). The board
  // derives "archived" from this — a resolved report older than 14 days.
  resolvedAt?: string | null;
  // Free-form dev-team working notes (editable at any status) + provenance.
  notes?: string | null;
  notesUpdatedBy?: string | null;
  notesUpdatedAt?: string | null;
  // Number of rolled-up email replies (email-intake threads). >0 → the card shows
  // a thread button that lazy-loads the full conversation. 0/undefined for normal reports.
  threadCount?: number;
  // Merge grouping. parentId = the group (parent) card this is filed under (null if
  // top-level). isGroup = this row IS a group container (contextApp='dev-team-group').
  parentId?: number | null;
  isGroup?: boolean;
  // File attachments captured with an inbox-triage email (empty for normal reports).
  attachments?: { id: string; name: string; contentType: string | null; size: number | null }[];
  // Captured diagnostics (from utils/bugLog) — attached at submit, shown to the
  // dev team, and forwarded to the shared sink once the backend is wired.
  logs?: unknown[];
  events?: unknown[];
  context?: Record<string, unknown>;
};

export type NewReport = {
  issue: string; description?: string; steps?: string; submittedBy: string; contextApp?: string;
  logs?: unknown[]; events?: unknown[]; context?: Record<string, unknown>;
};

// Bumped v1 -> v2 to drop the old seeded sample reports cached in browsers that
// loaded the earlier build. The real board reads from /api/dev-team; this local
// store is only the BugReporter's client-side record + an empty preview fallback.
const LS_KEY = 'devteam.bugReports.v2';

type Ctx = {
  reports: BugReport[];
  addReport: (r: NewReport) => BugReport;
  setStatus: (id: number, s: BugStatus) => void;
  setAssignee: (id: number, a: string | null) => void;
  setNotes: (id: number, notes: string) => void;
  removeReport: (id: number) => void;
};

const BugReportsCtx = createContext<Ctx>({ reports: [], addReport: () => ({} as BugReport), setStatus: () => {}, setAssignee: () => {}, setNotes: () => {}, removeReport: () => {} });
export const useBugReports = () => useContext(BugReportsCtx);

export function BugReportsProvider({ children }: { children: React.ReactNode }) {
  const [reports, setReports] = useState<BugReport[]>(() => {
    try { const s = localStorage.getItem(LS_KEY); if (s) return JSON.parse(s) as BugReport[]; } catch { /* ignore */ }
    return [];
  });
  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(reports)); } catch { /* quota — ignore */ } }, [reports]);

  const addReport = useCallback((r: NewReport): BugReport => {
    const rec: BugReport = {
      bugReportId: Date.now(),
      contextApp: r.contextApp || 'admin-hq',
      issue: r.issue,
      description: r.description,
      steps: r.steps,
      submittedBy: r.submittedBy,
      status: 'open',
      assignedTo: null,
      when: 'just now',
      logs: r.logs,
      events: r.events,
      context: r.context,
    };
    setReports(rs => [rec, ...rs]);
    return rec;
  }, []);
  const setStatus = useCallback((id: number, s: BugStatus) => setReports(rs => rs.map(x => x.bugReportId === id ? { ...x, status: s } : x)), []);
  const setAssignee = useCallback((id: number, a: string | null) => setReports(rs => rs.map(x => x.bugReportId === id ? { ...x, assignedTo: a } : x)), []);
  const setNotes = useCallback((id: number, notes: string) => setReports(rs => rs.map(x => x.bugReportId === id ? { ...x, notes, notesUpdatedBy: 'you', notesUpdatedAt: new Date().toISOString() } : x)), []);
  const removeReport = useCallback((id: number) => setReports(rs => rs.filter(x => x.bugReportId !== id)), []);

  return <BugReportsCtx.Provider value={{ reports, addReport, setStatus, setAssignee, setNotes, removeReport }}>{children}</BugReportsCtx.Provider>;
}
